import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import type { CollapseListener } from '@src/orders/CollapseListener';
import { EditOrderCollapsible } from '@src/orders/EditOrderCollapsible';
import type { FormEntries } from '@src/orders/FormEntries';
import { useSectionError } from '@src/orders/hooks/useSectionError';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import { FormCheckbox } from '@src/orders/PermitFormSections/FormCheckbox';
import { useDisabledFields } from '@src/orders/PermitFormSections/useDisabledFields';
import { accountFields, BilingualField } from '@src/permitField/AccountFields';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Props = { fields: string[] } & CollapseListener;

export const AccountSection = (props: Props) => {
  const { t } = useTranslation();
  const { isFieldDisabled } = useDisabledFields();
  const {
    register,
    getValues,
    formState: { errors, disabled: formDisabled },
  } = useFormContext<FormEntries<typeof props.fields>>();

  const { sectionInError } = useSectionError(Object.keys(accountFields));

  if (props.fields.length <= 0) {
    return null;
  }

  return (
    <EditOrderCollapsible
      title={t('accountNumbers')}
      id="account-section"
      collapseEventName={props.collapseEventName}
      uncollapseEventName={props.uncollapseEventName}
      inError={sectionInError}
    >
      <Stack gap={theme.spacing['3']}>
        <ConditionalField
          fields={props.fields}
          conditions={BilingualField.fieldName}
        >
          <FormCheckbox
            disabled={formDisabled || isFieldDisabled(BilingualField.fieldName)}
            register={register}
            errors={errors}
            fieldName={BilingualField.fieldName}
            defaultChecked={getValues(BilingualField.fieldName) as boolean}
          />
        </ConditionalField>
      </Stack>
    </EditOrderCollapsible>
  );
};
